// User Registration Page Component

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { RegistrationFormData } from './types/registration';
import { registrationService } from './services/registrationService';
import { validateRegistrationForm } from './utils/formValidation';
import { analyzePasswordStrength } from './utils/passwordValidation';
import PasswordStrengthMeter from './components/PasswordStrengthMeter';

// Styled Components
const RegisterContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  padding: theme.spacing(2),
}));

const RegisterCard = styled(Card)(({ theme }) => ({
  maxWidth: 600,
  width: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

interface RegisterPageProps {
  onRegisterSuccess?: () => void;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ onRegisterSuccess }) => {
  console.log('🚀 RegisterPage: Component initializing');

  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<RegistrationFormData>({
    username: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    firstName: '',
    lastName: '',
    displayName: '',
    phone: '',
    department: '',
    organization: '',
    acceptTerms: false,
    acceptPrivacy: false,
    role: 'user'
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // Password strength
  const passwordStrength = analyzePasswordStrength(formData.password);

  console.log('📊 RegisterPage: State:', {
    isLoading,
    error,
    success,
    passwordStrength: passwordStrength.strength
  });

  /**
   * Handle input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    console.log('📝 RegisterPage: Input change:', { name, value: type === 'checkbox' ? checked : value });

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Clear general error
    if (error) {
      setError(null);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📧 RegisterPage: Form submission started');

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    try {
      // Validate form
      const validation = validateRegistrationForm(formData);
      console.log('✅ RegisterPage: Validation result:', validation);

      if (!validation.isValid) {
        const errors: { [key: string]: string } = {};
        validation.errors.forEach(err => {
          errors[err.field] = err.message;
        });
        setFieldErrors(errors);
        setError('Please correct the errors in the form');
        setIsLoading(false);
        return;
      }

      // Prepare registration request
      const registrationRequest = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        displayName: formData.displayName?.trim() || `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone?.trim(),
        department: formData.department?.trim(),
        organization: formData.organization?.trim(),
        role: formData.role
      };

      console.log('🌐 RegisterPage: Sending registration request');

      // Call registration API
      const response = await registrationService.registerUser(registrationRequest);

      console.log('📥 RegisterPage: Registration response received:', response);

      if (response.success) {
        console.log('✅ RegisterPage: Registration successful');

        // Show success message
        if (response.requiresEmailVerification) {
          setSuccess(
            'Registration successful! Please check your email to verify your account.'
          );
        } else {
          setSuccess(
            'Registration successful! You can now log in with your credentials.'
          );
        }

        // Call success callback if provided
        if (onRegisterSuccess) {
          onRegisterSuccess();
        }

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);

      } else {
        console.log('❌ RegisterPage: Registration failed');
        setError(response.message || 'Registration failed. Please try again.');
      }

    } catch (err) {
      console.error('❌ RegisterPage: Registration error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      console.log('🏁 RegisterPage: Registration process finished');
      setIsLoading(false);
    }
  };

  /**
   * Navigate to login page
   */
  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('🔗 RegisterPage: Navigate to login');
    navigate('/login');
  };

  console.log('🎨 RegisterPage: Rendering component');

  return (
    <RegisterContainer>
      <RegisterCard>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Create Account
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Join us today and get started
            </Typography>
          </Box>

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Registration Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Username */}
            <TextField
              fullWidth
              name="username"
              label="Username"
              value={formData.username}
              onChange={handleInputChange}
              margin="normal"
              disabled={isLoading}
              error={!!fieldErrors.username}
              helperText={fieldErrors.username}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                )
              }}
            />

            {/* Email */}
            <TextField
              fullWidth
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              disabled={isLoading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                )
              }}
            />

            {/* First Name and Last Name */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                />
              </Grid>
            </Grid>

            {/* Password */}
            <TextField
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              disabled={isLoading}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Password Strength Meter */}
            {formData.password && (
              <PasswordStrengthMeter
                password={formData.password}
                strengthResult={passwordStrength}
                showRequirements={true}
              />
            )}

            {/* Confirm Password */}
            <TextField
              fullWidth
              name="passwordConfirmation"
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm Password"
              value={formData.passwordConfirmation}
              onChange={handleInputChange}
              margin="normal"
              disabled={isLoading}
              error={!!fieldErrors.passwordConfirmation}
              helperText={fieldErrors.passwordConfirmation}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Optional Fields Section */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Additional Information (Optional)
            </Typography>

            {/* Phone */}
            <TextField
              fullWidth
              name="phone"
              label="Phone Number"
              value={formData.phone}
              onChange={handleInputChange}
              margin="normal"
              disabled={isLoading}
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                )
              }}
            />

            {/* Department and Organization */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="department"
                  label="Department"
                  value={formData.department}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="organization"
                  label="Organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            {/* Terms and Privacy */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleInputChange}
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label={
                  <Typography variant="body2">
                    I accept the{' '}
                    <Link href="/terms" target="_blank" color="primary">
                      Terms of Service
                    </Link>
                  </Typography>
                }
              />
              {fieldErrors.acceptTerms && (
                <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>
                  {fieldErrors.acceptTerms}
                </Typography>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptPrivacy"
                    checked={formData.acceptPrivacy}
                    onChange={handleInputChange}
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label={
                  <Typography variant="body2">
                    I accept the{' '}
                    <Link href="/privacy" target="_blank" color="primary">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
              {fieldErrors.acceptPrivacy && (
                <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>
                  {fieldErrors.acceptPrivacy}
                </Typography>
              )}
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !passwordStrength.meetsRequirements || !formData.acceptTerms || !formData.acceptPrivacy}
              sx={{ mt: 3, mb: 2, height: 48 }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </Box>

          {/* Login Link */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                href="#"
                color="primary"
                fontWeight="medium"
                onClick={handleLoginClick}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default RegisterPage;
